import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Button from '../../components/ui/Button';
import { Activity, CheckCircle } from 'lucide-react';
export default function MentalCapacity() {
  return <main className="min-h-screen bg-white" data-id="element-1159">
      <Helmet data-id="element-1160">
        <title data-id="element-1161">Mental Capacity Assessments UK | Expert Psychiatrist</title>
        <meta name="description" content="Independent mental capacity assessments under the Mental Capacity Act 2005. COP3, DoLS, testamentary, and litigation capacity reports by Dr Imtiaz Syed." data-id="element-1162" />
        <meta name="keywords" content="Mental Capacity Assessments UK, Mental Capacity Act 2005, Capacity to Litigate, Testamentary Capacity, DoLS, COP3, Psychiatric capacity reports for COP3 court applications, Medico-Legal Psychiatry" data-id="element-1163" />
        <script type="application/ld+json" data-id="element-1164">
          {`
            {
              "@context": "https://schema.org",
              "@type": "MedicalService",
              "name": "Mental Capacity Assessments UK",
              "provider": {
                "@type": "Person",
                "name": "Dr. Imtiaz Syed",
                "jobTitle": "Psychiatrist & Expert Witness"
              },
              "serviceType": "Mental Capacity Assessment",
              "audience": {
                "@type": "Audience",
                "name": "Legal Professionals"
              },
              "availableChannel": {
                "@type": "ServiceChannel",
                "name": "Online and In-person",
                "serviceUrl": "https://medicolegal-psychiatry.co.uk/contact",
                "servicePhone": "",
                "serviceSmsNumber": ""
              },
              "serviceOutput": "Expert Mental Capacity Reports for Legal Proceedings"
            }
          `}
        </script>
      </Helmet>
      {/* Hero Section */}
      <section className="bg-primary text-white py-10 pt-24 md:pt-28" data-id="element-1165">
        <div className="container mx-auto px-4" data-id="element-1166">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold mb-4 text-center" data-id="element-1167">
            Mental Capacity Assessments UK
          </h1>
          <p className="text-xl max-w-3xl mx-auto text-center" data-id="element-1168">
            Essential Legal Clarity & Protection
          </p>
        </div>
      </section>
      {/* Main Content */}
      <section className="py-12" data-id="element-1169">
        <div className="container mx-auto px-4" data-id="element-1170">
          <div className="max-w-3xl mx-auto" data-id="element-1171">
            <p className="text-slate-dark text-lg mb-6" data-id="element-1172">
              In complex legal matters, a clear understanding of an individual's
              mental capacity is paramount. Dr. Imtiaz Syed provides meticulous,
              impartial, and expert Mental Capacity Assessments, strictly
              adhering to the Mental Capacity Act 2005. Our goal is to equip
              legal professionals with robust evidence, ensuring decisions align
              with an individual's best interests and uphold their rights.
            </p>
            <h2 className="text-2xl font-serif font-bold text-primary mb-4" data-id="element-1173">
              Why Instruct Dr. Imtiaz Syed for Mental Capacity Assessments?
            </h2>
            <p className="text-slate-dark mb-4" data-id="element-1174">
              Dr. Syed brings deep clinical expertise and legal acumen to every
              capacity assessment, ensuring:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8" data-id="element-1175">
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1176">
                <div className="flex items-start" data-id="element-1177">
                  <CheckCircle className="h-5 w-5 text-secondary mr-2 mt-1" data-id="element-1178" />
                  <div data-id="element-1179">
                    <h3 className="font-bold text-primary" data-id="element-1180">Legal Compliance</h3>
                    <p className="text-slate" data-id="element-1181">
                      All assessments strictly follow the Mental Capacity Act
                      2005 and relevant case law.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1182">
                <div className="flex items-start" data-id="element-1183">
                  <CheckCircle className="h-5 w-5 text-secondary mr-2 mt-1" data-id="element-1184" />
                  <div data-id="element-1185">
                    <h3 className="font-bold text-primary" data-id="element-1186">
                      Impartial & Objective Insights
                    </h3>
                    <p className="text-slate" data-id="element-1187">
                      Providing unbiased, evidence-based opinions you can trust.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1188">
                <div className="flex items-start" data-id="element-1189">
                  <CheckCircle className="h-5 w-5 text-secondary mr-2 mt-1" data-id="element-1190" />
                  <div data-id="element-1191">
                    <h3 className="font-bold text-primary" data-id="element-1192">
                      Thorough & Sensitive Approach
                    </h3>
                    <p className="text-slate" data-id="element-1193">
                      Assessments are comprehensive, conducted with the utmost
                      respect and empathy for the individual.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1194">
                <div className="flex items-start" data-id="element-1195">
                  <CheckCircle className="h-5 w-5 text-secondary mr-2 mt-1" data-id="element-1196" />
                  <div data-id="element-1197">
                    <h3 className="font-bold text-primary" data-id="element-1198">
                      Courtroom-Ready Reports
                    </h3>
                    <p className="text-slate" data-id="element-1199">
                      Clear, concise, and robust reports designed to withstand
                      scrutiny.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <h2 className="text-2xl font-serif font-bold text-primary mb-4" data-id="element-1200">
              Key Mental Capacity Assessments Provided
            </h2>
            <p className="text-slate-dark mb-4" data-id="element-1201">
              Dr. Syed offers expert evaluations across critical areas,
              including:
            </p>
            <ul className="space-y-3 mb-8 text-slate-dark" data-id="element-1202">
              <li className="flex items-start" data-id="element-1203">
                <span className="text-secondary font-bold mr-2" data-id="element-1204">•</span>
                <span data-id="element-1205">
                  <strong data-id="element-1206">Capacity to Litigate:</strong> Assessing the ability
                  to understand legal proceedings and instruct legal counsel.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1207">
                <span className="text-secondary font-bold mr-2" data-id="element-1208">•</span>
                <span data-id="element-1209">
                  <strong data-id="element-1210">Capacity for Residence & Care Decisions:</strong>{' '}
                  Evaluating the ability to choose living arrangements and care.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1211">
                <span className="text-secondary font-bold mr-2" data-id="element-1212">•</span>
                <span data-id="element-1213">
                  <strong data-id="element-1214">Capacity to Manage Property & Affairs (COP3):</strong>{' '}
                  Essential for financial decisions and Court of Protection
                  applications.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1215">
                <span className="text-secondary font-bold mr-2" data-id="element-1216">•</span>
                <span data-id="element-1217">
                  <strong data-id="element-1218">Testamentary Capacity:</strong> Determining mental
                  capacity for making or changing a will.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1219">
                <span className="text-secondary font-bold mr-2" data-id="element-1220">•</span>
                <span data-id="element-1221">
                  <strong data-id="element-1222">Capacity for Medical Treatment Decisions:</strong>{' '}
                  Assessing the ability to consent to or refuse medical care.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1223">
                <span className="text-secondary font-bold mr-2" data-id="element-1224">•</span>
                <span data-id="element-1225">
                  <strong data-id="element-1226">
                    Deprivation of Liberty Safeguards (DoLS) & Section 21A
                    Challenges:
                  </strong>{' '}
                  Expert assessments for care arrangements where liberty may be
                  restricted.
                </span>
              </li>
            </ul>
            <h2 className="text-2xl font-serif font-bold text-primary mb-4" data-id="element-1227">
              Our Seamless Process & Robust Reports
            </h2>
            <p className="text-slate-dark mb-6" data-id="element-1228">
              Our assessment process is thorough and efficient, involving
              careful review of documentation and sensitive clinical interviews.
              Dr. Syed is flexible, offering both secure remote assessments
              across the UK and in-person consultations in specific regions
              (West Midlands, East Midlands, Greater London, or by arrangement).
              The resulting reports are always clear, legally relevant, and
              provide precise findings and actionable insights for your case.
            </p>
            <h2 className="text-2xl font-serif font-bold text-primary mb-4" data-id="element-1229">
              Partnering for Just Outcomes
            </h2>
            <p className="text-slate-dark mb-6" data-id="element-1230">
              Dr. Imtiaz Syed is your trusted partner for navigating mental
              capacity complexities. Rely on his expertise to provide the
              clarity needed for informed legal decisions and to ensure
              individual rights are always protected.
            </p>

            {/* Related Services */}
            <div className="mb-10 border-t border-gray-200 pt-8" data-id="element-1231">
              <h3 className="text-xl font-serif font-bold text-primary mb-4" data-id="element-1232">
                Explore Our Other Services
              </h3>
              <div className="flex flex-col sm:flex-row flex-wrap gap-4" data-id="element-1233">
                <Link to="/services/forensic-risk" className="text-secondary hover:text-primary font-medium underline transition-colors" data-id="element-1234">
                  Forensic & Risk Assessments
                </Link>
                <Link to="/services/specialist" className="text-secondary hover:text-primary font-medium underline transition-colors" data-id="element-1235">
                  Psychiatric Reports for Courts & Tribunals
                </Link>
                <Link to="/services/clinical-negligence" className="text-secondary hover:text-primary font-medium underline transition-colors" data-id="element-1236">
                  Clinical Negligence Cases
                </Link>
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg shadow-md mb-8" data-id="element-1237">
              <h3 className="text-xl font-serif font-bold text-primary mb-4" data-id="element-1238">
                Ready to Instruct Dr. Syed?
              </h3>
              <p className="text-slate-dark mb-4" data-id="element-1239">
                We invite you to discuss your specific case requirements.
              </p>
              <div className="flex items-center" data-id="element-1240">
                <span className="mr-2 text-slate-dark" data-id="element-1241">Contact Us:</span>
                <a href="mailto:enquiries@medicolegal-psychiatry.co.uk" className="text-primary hover:text-secondary transition-colors" data-id="element-1242">
                  enquiries@medicolegal-psychiatry.co.uk
                </a>
              </div>
            </div>
            <div className="text-center" data-id="element-1243">
              <Button variant="primary" size="lg" href="/contact" data-id="element-1244">
                Book a Mental Capacity Assessment
              </Button>
            </div>
          </div>
        </div>
      </section>
    </main>;
}